'use client'

import { SelectOption as SelectOptionType } from '../../../types'
import React, { ReactElement, SelectHTMLAttributes } from 'react'
import { MultiSelectCombobox } from './_partials/MultiSelectCombobox'
import { SingleSelectCombobox } from './_partials/SingleSelectCombobox'

export interface HTMLSelectInputProps
    extends Pick<
        SelectHTMLAttributes<HTMLSelectElement>,
        'className' | 'disabled' | 'name' | 'id' | 'required' | 'multiple'
    > {
    defaultValue?: string | number | string[] | number[]
    options: SelectOptionType[]
    clearable?: boolean
    isLoading?: boolean
    error?: boolean
    'data-testid'?: string
}

/**
 * Plain HTML select that works with native form submission and validation.
 *
 * **Single select:** Renders a custom listbox (combobox) with styled options (li role="option"),
 * type-ahead, full keyboard support, form reset, and programmatic value via setSelectInputValue.
 *
 * **Multi-select (multiple=true):** Renders a custom combobox with type-ahead, full keyboard support,
 * form reset handling, and programmatic value via setSelectInputValue. See MultiSelectCombobox for details.
 *
 * **Setting value programmatically:** Use `setSelectInputValue` from this module. Give the select an `id`;
 * then call `setSelectInputValue(id, value)`.
 */
export const HTMLSelectInput = ({ multiple, ...rest }: HTMLSelectInputProps): ReactElement => {
    if (multiple) {
        return <MultiSelectCombobox {...rest} />
    }

    return <SingleSelectCombobox {...rest} />
}
